import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';

// Shape of the scenario artifact the AI service emits (kind: "scenario_diff").
// It is the trimmed scenario result — the diff carries everything we chart.
interface WeightDelta { name: string; base_weight: number; scenario_weight: number; delta: number; }
interface Mover { identifier: string; name: string; base_weight: number; scenario_weight: number; delta: number; }
interface Pair { base: number; scenario: number; delta: number; }
interface Resolution { action: string; ref: string; status: string; detail?: string; candidates?: unknown[]; }

interface ScenarioDiff {
  totals: { base_value: number; scenario_value: number; delta: number; delta_pct: number | null };
  positions: { added: unknown[]; removed: unknown[]; changed: unknown[] };
  sectors: WeightDelta[];
  countries: WeightDelta[];
  concentration: { effective_n: Pair; top5_weight: Pair; top10_weight: Pair };
  top_holdings: Mover[];
}

interface PositionReturn {
  kind: string; identifier: string; name: string;
  value_before: number; value_after: number; return_pct: number;
}

export interface ScenarioDiffData {
  currency: string;
  diff: ScenarioDiff;
  // Present for market shocks: how each held position revalues.
  position_returns?: PositionReturn[];
  resolution: Resolution[];
}

const NAVY = '#000537';
const GREY = '#a1a1aa';

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
const ppt = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)} ppt`;

const money = (v: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);

const deltaColor = (v: number) => (v > 0 ? '#15803d' : v < 0 ? '#b91c1c' : '#52525b');

const ScenarioDiffChart: React.FC<{ data: ScenarioDiffData }> = ({ data }) => {
  const { currency, diff, resolution } = data;
  const unresolved = (resolution || []).filter((r) => r.status !== 'resolved');

  // Top sectors by magnitude of shift, before vs after.
  const sectors = [...(diff.sectors || [])]
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 6)
    .map((s) => ({ name: s.name, Before: s.base_weight, After: s.scenario_weight }));

  const movers = (diff.top_holdings || []).slice(0, 6);
  const countries = [...(diff.countries || [])]
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 4);

  // Market shocks carry per-position returns; show the biggest movers by |return|.
  const positionReturns = [...(data.position_returns || [])]
    .sort((a, b) => Math.abs(b.return_pct) - Math.abs(a.return_pct))
    .slice(0, 8);

  const label: React.CSSProperties = { fontSize: '0.7rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.03em' };

  return (
    <div className="chart-card">
      {/* Totals header */}
      <div className="d-flex justify-content-between align-items-baseline" style={{ marginBottom: 14 }}>
        <span className="fw-semibold" style={{ fontSize: '0.85rem', color: NAVY }}>Scenario result</span>
        <span style={{ fontSize: '0.8rem', color: '#3f3f46' }}>
          {money(diff.totals.base_value, currency)} <span style={{ color: GREY }}>→</span>{' '}
          <span className="fw-semibold">{money(diff.totals.scenario_value, currency)}</span>{' '}
          <span style={{ color: deltaColor(diff.totals.delta) }}>
            ({diff.totals.delta >= 0 ? '+' : ''}{money(diff.totals.delta, currency)})
          </span>
        </span>
      </div>

      {/* Sector before/after */}
      {sectors.length > 0 && (
        <>
          <div style={label}>Sector exposure (before → after)</div>
          <ResponsiveContainer width="100%" height={Math.max(140, sectors.length * 34)}>
            <BarChart layout="vertical" data={sectors} margin={{ top: 6, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid horizontal={false} stroke="#f1f1f3" />
              <XAxis type="number" tickFormatter={pct} tick={{ fontSize: 11, fill: '#71717a' }} />
              <YAxis type="category" dataKey="name" width={104} tick={{ fontSize: 11, fill: '#3f3f46' }} />
              <Tooltip formatter={(v: number) => pct(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Before" fill={GREY} radius={[0, 3, 3, 0]} />
              <Bar dataKey="After" fill={NAVY} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {/* Concentration + country shift */}
      <div className="d-flex flex-wrap" style={{ gap: 18, marginTop: 12 }}>
        <div>
          <div style={label}>Concentration</div>
          <div style={{ fontSize: '0.8rem', color: '#3f3f46', marginTop: 4 }}>
            Effective N: {diff.concentration.effective_n.base} <span style={{ color: GREY }}>→</span>{' '}
            <span className="fw-semibold">{diff.concentration.effective_n.scenario}</span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#3f3f46' }}>
            Top-5: {pct(diff.concentration.top5_weight.base)} <span style={{ color: GREY }}>→</span>{' '}
            <span className="fw-semibold">{pct(diff.concentration.top5_weight.scenario)}</span>
          </div>
        </div>
        {countries.length > 0 && (
          <div>
            <div style={label}>Country shift</div>
            {countries.map((c) => (
              <div key={c.name} style={{ fontSize: '0.8rem', color: '#3f3f46', marginTop: 2 }}>
                {c.name}: <span style={{ color: deltaColor(c.delta) }}>{ppt(c.delta)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top movers */}
      {movers.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={label}>Biggest holding shifts</div>
          <table style={{ width: '100%', fontSize: '0.78rem', marginTop: 4, borderCollapse: 'collapse' }}>
            <tbody>
              {movers.map((m) => (
                <tr key={m.identifier} style={{ borderTop: '1px solid #f1f1f3' }}>
                  <td style={{ padding: '3px 0', color: '#18181b' }}>{m.name}</td>
                  <td style={{ padding: '3px 8px', textAlign: 'right', color: '#71717a' }}>
                    {pct(m.base_weight)} → {pct(m.scenario_weight)}
                  </td>
                  <td style={{ padding: '3px 0', textAlign: 'right', color: deltaColor(m.delta), whiteSpace: 'nowrap' }}>
                    {ppt(m.delta)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-position returns (market shocks): each held position's move */}
      {positionReturns.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={label}>How your positions move</div>
          <table style={{ width: '100%', fontSize: '0.78rem', marginTop: 4, borderCollapse: 'collapse' }}>
            <tbody>
              {positionReturns.map((p) => (
                <tr key={`${p.kind}:${p.identifier}`} style={{ borderTop: '1px solid #f1f1f3' }}>
                  <td style={{ padding: '3px 0', color: '#18181b' }}>
                    {p.name}{' '}
                    <span style={{ color: '#a1a1aa', fontSize: '0.7rem', textTransform: 'uppercase' }}>{p.kind}</span>
                  </td>
                  <td style={{ padding: '3px 8px', textAlign: 'right', color: '#71717a' }}>
                    {money(p.value_before, currency)} → {money(p.value_after, currency)}
                  </td>
                  <td style={{ padding: '3px 0', textAlign: 'right', color: deltaColor(p.return_pct), whiteSpace: 'nowrap' }}>
                    {pct(p.return_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Unresolved instruments */}
      {unresolved.length > 0 && (
        <div style={{ marginTop: 12, fontSize: '0.78rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '7px 10px' }}>
          {unresolved.map((r, i) => (
            <div key={i}>
              “{r.ref}” — {r.status}{r.detail ? `: ${r.detail}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScenarioDiffChart;
